// src/api/mockHrConsole.js
//
// SOLO SVILUPPO — non collaudo della demo del 23/09 (indicazione esplicita di
// Daniele: non usare fixture DEMO_ come collaudo). Serve unicamente a
// verificare che ResourceSelfAssessmentPanel e HrSelfAssessmentConsole si
// comportino correttamente su tutti gli stati/scenari, mentre restiamo
// bloccati sul dato reale (ingestion/raccordo/fitting da verificare via
// Monaco). Prefisso DEV_, non DEMO_, per non confonderlo con le fixture SA
// già usate altrove nell'app.
//
// Copre deliberatamente anche i casi limite che il vero backend potrebbe
// restituire: campi assenti (N/D in tabella), righe non invitabili, ed
// esclusioni nell'anteprima dry_run.

const RESOURCES = {
  DEV_R2_001: { resource_id: 'DEV_R2_001', stato: 'R2' },
  DEV_R3A_001: { resource_id: 'DEV_R3A_001', stato: 'R3a' },
  DEV_R3B_001: { resource_id: 'DEV_R3B_001', stato: 'R3b' },
  DEV_R3C_001: { resource_id: 'DEV_R3C_001', stato: 'R3c' },
  DEV_R1_001: { resource_id: 'DEV_R1_001', stato: 'R1' },
  DEV_R4_001: { resource_id: 'DEV_R4_001', stato: 'R4' },
  // Ha già un'istanza SA aperta: usata per collaudare il 409 e il flusso di
  // sollecito (Flusso alternativo 4, UC-SA-01) sia nel pannello singolo che
  // nella console.
  DEV_R2_002: { resource_id: 'DEV_R2_002', stato: 'R2', _hasOpenInstance: true },
  DEV_R1_002: { resource_id: 'DEV_R1_002', stato: 'R1' },
};

const LONGLIST_ROWS = [
  { resource_id: 'DEV_R2_001', candidate_name: 'M. Rossi (dev)', hybrid_score: 82, stato: 'R2', profilo_sa: 'SA-1', invitabile: true },
  { resource_id: 'DEV_R3A_001', candidate_name: 'L. Bianchi (dev)', hybrid_score: 74, stato: 'R3a', profilo_sa: 'SA-2a', invitabile: true },
  { resource_id: 'DEV_R3B_001', candidate_name: 'S. Verdi (dev)', hybrid_score: 91, stato: 'R3b', profilo_sa: 'SA-2b', invitabile: true },
  { resource_id: 'DEV_R3C_001', candidate_name: 'F. Neri (dev)', hybrid_score: 68, stato: 'R3c', profilo_sa: 'SA-2c', invitabile: true },
  // Riga selezionabile in longlist (sembra ok): la scoperta che ha già
  // un'istanza aperta avviene solo al momento del dry-run/invio, come nel
  // caso reale descritto nell'Analisi Funzionale (Flusso alternativo 4).
  { resource_id: 'DEV_R2_002', candidate_name: 'A. Gialli (dev)', hybrid_score: 77, stato: 'R2', profilo_sa: 'SA-1', invitabile: true },
  // Riga NON invitabile già in longlist (motivo diverso: stato non ammesso,
  // non istanza aperta) — per collaudare il badge "Non invitabile" e la
  // checkbox disabilitata fin da subito.
  { resource_id: 'DEV_R1_002', candidate_name: 'P. Blu (dev)', hybrid_score: 60, stato: 'R1', profilo_sa: null, invitabile: false },
  // Riga deliberatamente incompleta: simula un payload backend che non
  // restituisce ancora stato/profilo/invitabilità — deve comparire come
  // N/D e non selezionabile, mai un valore inventato.
  { resource_id: null, candidate_name: 'Riga con campi assenti (dev)', hybrid_score: 55, stato: null, profilo_sa: null, invitabile: null },
];

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class MockHrConsoleError extends Error {
  constructor(status, detail) {
    super(detail);
    this.status = status;
    this.detail = detail;
    this.technicalMessage = `[DEV mock] HTTP ${status}`;
  }
}

export function createMockHrConsoleClient() {
  return {
    async get(path) {
      await delay(250);

      const resourceMatch = path.match(/^\/resources\/([^/?]+)/);
      if (resourceMatch) {
        const id = decodeURIComponent(resourceMatch[1]);
        const resource = RESOURCES[id];
        if (!resource) {
          throw new MockHrConsoleError(404, `[DEV mock] risorsa '${id}' inesistente`);
        }
        return resource;
      }

      const longlistMatch = path.match(/^\/targets\/([^/?]+)\/longlist/);
      if (longlistMatch) {
        // Qualsiasi jd_id restituisce lo stesso set di fixture, tranne
        // "DEV_EMPTY" per collaudare anche il caso lista vuota.
        const jdId = decodeURIComponent(longlistMatch[1]);
        if (jdId === 'DEV_EMPTY') return [];
        return LONGLIST_ROWS;
      }

      throw new MockHrConsoleError(404, `[DEV mock] path non gestito: ${path}`);
    },

    async post(path, body) {
      await delay(350);

      if (path === '/hr/token') {
        const { entity_id } = body || {};
        const resource = entity_id && RESOURCES[entity_id];
        if (!resource) {
          throw new MockHrConsoleError(404, '[DEV mock] risorsa non trovata per generazione token');
        }
        if (resource._hasOpenInstance) {
          throw new MockHrConsoleError(409, '[DEV mock] istanza già aperta per questa risorsa');
        }
        return {
          link: `https://test.isaitalia.ai/self-assessment?token=dev-mock-${entity_id.toLowerCase()}`,
          ttl_hours: 72,
        };
      }

      const inviteMatch = path.match(/^\/hr\/invite\/([^/?]+)/);
      if (inviteMatch) {
        const { resource_ids = [], dry_run, sollecito } = body || {};

        if (sollecito) {
          // [DEV mock] Flusso alternativo 4 (UC-SA-01): reminder immediato
          // su istanza già aperta, nessuna nuova istanza creata.
          const id = resource_ids[0];
          if (!id || !RESOURCES[id]) {
            throw new MockHrConsoleError(404, '[DEV mock] risorsa non trovata per il sollecito');
          }
          return { ok: true, data_ultimo_sollecito: new Date().toLocaleDateString('it-IT') };
        }

        const ammessi = resource_ids.filter((id) => {
          const r = RESOURCES[id];
          return r && r.stato !== 'R1' && r.stato !== 'R4' && !r._hasOpenInstance;
        });
        const esclusi = resource_ids
          .filter((id) => !ammessi.includes(id))
          .map((id) => {
            const r = RESOURCES[id];
            const motivo = r?._hasOpenInstance
              ? '[DEV mock] istanza già aperta'
              : '[DEV mock] stato non ammesso';
            return { resource_id: id, motivo };
          });

        if (dry_run) {
          return { invitabili: ammessi, esclusi };
        }
        return { invitati: ammessi, esclusi };
      }

      throw new MockHrConsoleError(404, `[DEV mock] path non gestito: ${path}`);
    },

    async patch(path, body) {
      await delay(300);

      const stateMatch = path.match(/^\/resources\/([^/?]+)\/state/);
      if (stateMatch) {
        const id = decodeURIComponent(stateMatch[1]);
        const resource = RESOURCES[id];
        if (!resource) {
          throw new MockHrConsoleError(404, `[DEV mock] risorsa '${id}' inesistente`);
        }

        const { to_state } = body || {};
        // Stessa regola di ResourceStateManager.jsx: R2->R3/R4, R3x->R4.
        const allowedFrom = resource.stato === 'R2'
          ? ['R3', 'R4']
          : (resource.stato || '').startsWith('R3') ? ['R4'] : [];

        if (!allowedFrom.includes(to_state)) {
          throw new MockHrConsoleError(422, `[DEV mock] transizione ${resource.stato} → ${to_state} non ammessa`);
        }

        // Il mock atterra un "R3" generico su "R3a" di default: nel reale i
        // sottostati sono derivati dal backend, qui li simuliamo così.
        resource.stato = to_state === 'R3' ? 'R3a' : to_state;
        return { resource_id: id, stato: resource.stato };
      }

      throw new MockHrConsoleError(501, `[DEV mock] PATCH non gestita per: ${path}`);
    },
  };
}
